import React, { useState, useCallback, useRef, useEffect } from "react";
import Cropper from "react-easy-crop";

const OUTPUT_SIZE = 400;
const MIN_ZOOM = 1;
const MAX_ZOOM = 5;

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    if (typeof src === "string" && /^https?:/i.test(src)) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

/** PNG carries an alpha channel: re-encoding it to JPEG turns transparent pixels black. */
const isPngSource = (source) => {
  if (!source) return false;
  if (typeof source === "string") return /^data:image\/png[;,]/i.test(source);
  return String(source.type || "").toLowerCase() === "image/png";
};

/**
 * Paint an opaque white base before drawing.
 * A transparent canvas encodes to black in JPEG, and the stored player image is
 * served as .jpg — so alpha must be flattened here, on the client, or the
 * cut-out areas of a PNG come back black. No-op for images that are fully opaque.
 */
const fillWhiteBase = (ctx, width, height) => {
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
};

/** Local only — normalize EXIF so pixels match what user sees. */
async function normalizeImageOrientation(fileOrDataUrl) {
  try {
    let bitmapSource = fileOrDataUrl;
    let keepAlpha = isPngSource(fileOrDataUrl);
    if (typeof fileOrDataUrl === "string") {
      if (
        fileOrDataUrl.startsWith("data:") ||
        fileOrDataUrl.startsWith("blob:")
      ) {
        const res = await fetch(fileOrDataUrl);
        bitmapSource = await res.blob();
        keepAlpha = keepAlpha || isPngSource(bitmapSource);
      } else {
        return fileOrDataUrl;
      }
    }
    if (typeof createImageBitmap === "function") {
      const bitmap = await createImageBitmap(bitmapSource, {
        imageOrientation: "from-image",
      });
      const canvas = document.createElement("canvas");
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext("2d");
      fillWhiteBase(ctx, canvas.width, canvas.height);
      ctx.drawImage(bitmap, 0, 0);
      bitmap.close?.();
      // Alpha is already flattened above, so PNG vs JPEG is only a quality choice:
      // keep PNG lossless for PNG uploads, JPEG for everything else (unchanged).
      return keepAlpha
        ? canvas.toDataURL("image/png")
        : canvas.toDataURL("image/jpeg", 0.92);
    }
  } catch (err) {
    console.warn("Orientation normalize failed", err);
  }
  if (typeof fileOrDataUrl === "string") return fileOrDataUrl;
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(fileOrDataUrl);
  });
}

function isSkinPixel(r, g, b) {
  const y = 0.299 * r + 0.587 * g + 0.114 * b;
  const cb = 128 - 0.168736 * r - 0.331264 * g + 0.5 * b;
  const cr = 128 + 0.5 * r - 0.418688 * g - 0.081312 * b;
  return (
    y > 40 &&
    y < 250 &&
    cb > 77 &&
    cb < 127 &&
    cr > 133 &&
    cr < 173 &&
    r > 60 &&
    g > 30 &&
    b > 15
  );
}

function isDarkFeature(r, g, b) {
  // Hair / sunglasses / beard — helps when skin is covered
  const y = 0.299 * r + 0.587 * g + 0.114 * b;
  return y < 55 && Math.max(r, g, b) - Math.min(r, g, b) < 40;
}

/** Chrome/Edge built-in — free, local, no network. */
async function detectWithNativeFaceDetector(img) {
  try {
    if (typeof window === "undefined" || !("FaceDetector" in window)) {
      return null;
    }
    const detector = new window.FaceDetector({
      fastMode: true,
      maxDetectedFaces: 5,
    });
    const faces = await detector.detect(img);
    if (!faces?.length) return null;
    return faces
      .map((f) => {
        const b = f.boundingBox;
        return {
          x: Number(b.x) || 0,
          y: Number(b.y) || 0,
          width: Number(b.width) || 0,
          height: Number(b.height) || 0,
        };
      })
      .filter((b) => b.width > 12 && b.height > 12)
      .reduce((a, b) =>
        a.width * a.height >= b.width * b.height ? a : b
      );
  } catch (err) {
    console.warn("FaceDetector failed", err);
    return null;
  }
}

/**
 * Local head finder (no CDN / no paid API).
 * Scores skin + dark features (sunglasses/hair) in the UPPER half —
 * works when FaceDetector misses or glasses cover eyes.
 */
function detectHeadByLocalScript(img) {
  try {
    const maxDim = 200;
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);

    const cell = 4;
    const cols = Math.ceil(w / cell);
    const rows = Math.ceil(h / cell);
    // Heads almost always sit in top ~50% for profile / standing photos
    const rowEnd = Math.ceil(rows * 0.55);
    const scores = new Float32Array(cols * rows);

    for (let gy = 0; gy < rowEnd; gy++) {
      for (let gx = 0; gx < cols; gx++) {
        let skin = 0;
        let dark = 0;
        let edge = 0;
        const y0 = gy * cell;
        const x0 = gx * cell;
        const y1 = Math.min(h, y0 + cell);
        const x1 = Math.min(w, x0 + cell);
        for (let y = y0; y < y1; y++) {
          for (let x = x0; x < x1; x++) {
            const i = (y * w + x) * 4;
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            if (isSkinPixel(r, g, b)) skin += 1;
            else if (isDarkFeature(r, g, b)) dark += 1;
            // Simple horizontal edge (hairline / glasses frame)
            if (x + 1 < x1) {
              const j = (y * w + (x + 1)) * 4;
              const d =
                Math.abs(r - data[j]) +
                Math.abs(g - data[j + 1]) +
                Math.abs(b - data[j + 2]);
              if (d > 60) edge += 1;
            }
          }
        }
        // Prefer upper band where heads are; don't prefer extreme top (sky)
        const band = gy / rows;
        const bandW =
          band >= 0.05 && band <= 0.4 ? 1.35 : band < 0.05 ? 0.6 : 1.0;
        scores[gy * cols + gx] = (skin * 1.4 + dark * 1.1 + edge * 0.35) * bandW;
      }
    }

    let best = { score: 0, gx: Math.floor(cols / 2), gy: Math.floor(rows * 0.22) };
    for (let gy = 1; gy < rowEnd - 1; gy++) {
      for (let gx = 1; gx < cols - 1; gx++) {
        let s = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            s += scores[(gy + dy) * cols + (gx + dx)];
          }
        }
        if (s > best.score) best = { score: s, gx, gy };
      }
    }

    if (best.score < 25) return null;

    // Blob around peak
    const threshold = best.score * 0.32;
    let minGx = best.gx;
    let maxGx = best.gx;
    let minGy = best.gy;
    let maxGy = best.gy;
    for (let gy = 0; gy < rowEnd; gy++) {
      for (let gx = 0; gx < cols; gx++) {
        let s = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const yy = gy + dy;
            const xx = gx + dx;
            if (yy < 0 || xx < 0 || yy >= rows || xx >= cols) continue;
            s += scores[yy * cols + xx];
          }
        }
        if (s < threshold) continue;
        if (Math.abs(gx - best.gx) > cols * 0.25) continue;
        if (Math.abs(gy - best.gy) > rows * 0.22) continue;
        minGx = Math.min(minGx, gx);
        maxGx = Math.max(maxGx, gx);
        minGy = Math.min(minGy, gy);
        maxGy = Math.max(maxGy, gy);
      }
    }

    // Expand to include chin below hotspot
    const blobH = Math.max(1, maxGy - minGy + 1);
    const blobW = Math.max(1, maxGx - minGx + 1);
    maxGy = Math.min(rows - 1, maxGy + Math.ceil(blobH * 0.9));
    minGx = Math.max(0, minGx - Math.ceil(blobW * 0.15));
    maxGx = Math.min(cols - 1, maxGx + Math.ceil(blobW * 0.15));

    const x0 = (minGx * cell) / scale;
    const y0 = (minGy * cell) / scale;
    const x1 = ((maxGx + 1) * cell) / scale;
    const y1 = ((maxGy + 1) * cell) / scale;

    return {
      x: x0,
      y: y0,
      width: Math.max(16, x1 - x0),
      height: Math.max(16, y1 - y0),
    };
  } catch {
    return null;
  }
}

async function detectMainFaceBox(img) {
  const native = await detectWithNativeFaceDetector(img);
  if (native) return { ...native, source: "native" };
  const local = detectHeadByLocalScript(img);
  if (local) return { ...local, source: "local" };
  return null;
}

/** Always returns a square crop area in image pixels. */
async function computeDefaultFaceCrop(imageSrc) {
  const img = await loadImage(imageSrc);
  const imgW = img.naturalWidth || img.width;
  const imgH = img.naturalHeight || img.height;
  const minSide = Math.min(imgW, imgH);

  const faceBox = await detectMainFaceBox(img);

  let faceMidX;
  let faceMidY;
  let faceSize;

  if (faceBox) {
    const faceW = faceBox.width;
    const faceH = faceBox.height;
    faceSize = Math.max(faceW, faceH);
    // Full head span: a bit above top → below chin
    const top = faceBox.y - faceH * 0.25;
    const bottom = faceBox.y + faceH * 1.2;
    faceMidX = faceBox.x + faceW / 2;
    faceMidY = (top + bottom) / 2;
    faceSize = Math.max(faceSize, bottom - top);
  } else {
    // Portrait fallback: heads sit in upper-center (NOT image center / torso)
    faceMidX = imgW * 0.5;
    faceMidY = imgH * 0.28;
    faceSize = minSide * 0.42;
  }

  // Face fills ~40% of square → profile-style framing
  let size = Math.max(faceSize * 2.4, minSide / MAX_ZOOM);
  size = Math.min(size, minSide);

  let x = faceMidX - size / 2;
  let y = faceMidY - size / 2;
  x = Math.max(0, Math.min(x, imgW - size));
  y = Math.max(0, Math.min(y, imgH - size));

  return {
    x: Math.round(x),
    y: Math.round(y),
    width: Math.round(size),
    height: Math.round(size),
    foundFace: Boolean(faceBox),
  };
}

const ImageCropper = ({
  defaultImage,
  setCroppedImage,
  croppedImage,
  setOriginalFileName,
}) => {
  const fileInputRef = useRef(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(defaultImage);
  const [containerHeight, setContainerHeight] = useState("auto");
  const [initialCroppedAreaPixels, setInitialCroppedAreaPixels] =
    useState(null);
  const [cropperKey, setCropperKey] = useState(0);
  const [isPreparingCrop, setIsPreparingCrop] = useState(false);

  const onSelectFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (setOriginalFileName) {
      setOriginalFileName(file.name);
    }

    (async () => {
      setIsPreparingCrop(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setInitialCroppedAreaPixels(null);

      try {
        const orientedDataUrl = await normalizeImageOrientation(file);
        const faceCrop = await computeDefaultFaceCrop(orientedDataUrl);

        // Set initial crop BEFORE showing cropper so library applies it on mount
        setInitialCroppedAreaPixels(faceCrop);
        setImageToCrop(orientedDataUrl);
        setCropperKey((k) => k + 1);
        setShowCropper(true);
      } catch (err) {
        console.error("Image prepare failed", err);
      } finally {
        setIsPreparingCrop(false);
      }
    })();

    e.target.value = "";
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const onCropComplete = useCallback((_area, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleCrop = async () => {
    if (!imageToCrop || !croppedAreaPixels) return;
    const next = await getCroppedImg(imageToCrop, croppedAreaPixels);
    setCroppedImage(next);
    setShowCropper(false);
  };

  const getCroppedImg = (imageSrc, area) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const image = new Image();
    image.src = imageSrc;

    return new Promise((resolve, reject) => {
      image.onload = () => {
        canvas.width = OUTPUT_SIZE;
        canvas.height = OUTPUT_SIZE;
        // Guarantees opaque output even when orientation normalization was
        // skipped (non-local src) or fell back to the raw data URL.
        fillWhiteBase(ctx, OUTPUT_SIZE, OUTPUT_SIZE);
        ctx.drawImage(
          image,
          area.x,
          area.y,
          area.width,
          area.height,
          0,
          0,
          OUTPUT_SIZE,
          OUTPUT_SIZE,
        );
        resolve(canvas.toDataURL("image/png", 0.8));
      };
      image.onerror = reject;
    });
  };

  useEffect(() => {
    const calculateOptimalHeight = () => {
      setContainerHeight(Math.min(window.innerHeight * 0.9, 700));
    };
    calculateOptimalHeight();
    window.addEventListener("resize", calculateOptimalHeight);
    return () => window.removeEventListener("resize", calculateOptimalHeight);
  }, []);

  return (
    <>
      {isPreparingCrop && (
        <div className="my-cropper-overlay">
          <div className="face-prep-toast">Centering face…</div>
        </div>
      )}
      {showCropper && initialCroppedAreaPixels && (
        <div className="my-cropper-overlay">
          <div
            className="my-cropper-container"
            style={{ height: containerHeight }}
          >
            <div className="cropper-header">
              <h2>Crop Your Image</h2>
              <button
                className="close-button"
                onClick={() => setShowCropper(false)}
                aria-label="Close cropper"
                type="button"
              >
                ✕
              </button>
            </div>

            <div className="cropper-content">
              <div className="cropper-area">
                <Cropper
                  key={cropperKey}
                  image={imageToCrop}
                  crop={crop}
                  zoom={zoom}
                  minZoom={MIN_ZOOM}
                  maxZoom={MAX_ZOOM}
                  aspect={1}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                  showGrid={true}
                  initialCroppedAreaPixels={initialCroppedAreaPixels}
                />
              </div>

              <div className="cropper-controls">
                <div className="zoom-control">
                  <label htmlFor="zoom-range">Zoom</label>
                  <input
                    type="range"
                    id="zoom-range"
                    min={MIN_ZOOM}
                    max={MAX_ZOOM}
                    step={0.1}
                    value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value))}
                    className="zoom-slider"
                  />
                </div>

                <div className="action-buttons">
                  <button
                    onClick={handleCrop}
                    className="crop-button"
                    type="button"
                  >
                    Apply Crop
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="cropper-container">
        <div className="img-container" onClick={triggerFileInput}>
          <img
            src={croppedImage || defaultImage || imageToCrop}
            alt="Preview"
            className="preview-image"
          />
        </div>
        <input
          type="file"
          style={{ display: "none" }}
          ref={fileInputRef}
          className="file-input"
          onChange={onSelectFile}
          accept="image/*"
          aria-label="Select image to crop"
        />
      </div>

      <style jsx>{`
        .my-cropper-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.7);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          padding: 20px;
          box-sizing: border-box;
          animation: fadeIn 0.3s ease-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .face-prep-toast {
          background: #fff;
          color: #333;
          padding: 14px 22px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 15px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
        }

        .my-cropper-container {
          background-color: white;
          border-radius: 16px;
          width: 100%;
          max-width: 650px;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.2);
          border: 1px solid rgba(0, 0, 0, 0.1);
        }

        .cropper-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px 20px;
          border-bottom: 1px solid #e8e8e8;
          background-color: #f9f9f9;
          flex-shrink: 0;
        }

        .cropper-header h2 {
          margin: 0;
          font-size: 18px;
          color: #333;
          font-weight: 600;
        }

        .close-button {
          background: none;
          border: none;
          font-size: 24px;
          color: #888;
          cursor: pointer;
          transition: color 0.2s ease;
          padding: 0;
          line-height: 1;
        }

        .close-button:hover {
          color: #000;
        }

        .cropper-content {
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          overflow: hidden;
        }

        .cropper-area {
          position: relative;
          flex-grow: 1;
          min-height: 300px;
          background: #f4f4f4;
          overflow: hidden;
        }

        .cropper-controls {
          padding: 15px 20px;
          border-top: 1px solid #e8e8e8;
          background-color: #f9f9f9;
          flex-shrink: 0;
        }

        .zoom-control {
          display: flex;
          align-items: center;
          margin-bottom: 15px;
        }

        .zoom-control label {
          margin-right: 15px;
          font-size: 14px;
          color: #666;
          min-width: 50px;
        }

        .zoom-slider {
          flex-grow: 1;
          appearance: none;
          width: 100%;
          height: 8px;
          background: #e0e0e0;
          outline: none;
          opacity: 0.7;
          transition: opacity 0.2s;
          border-radius: 4px;
        }

        .zoom-slider::-webkit-slider-thumb {
          appearance: none;
          width: 18px;
          height: 18px;
          background: #4caf50;
          cursor: pointer;
          border-radius: 50%;
          box-shadow: 0 0 0 4px rgba(76, 175, 80, 0.2);
        }

        .action-buttons {
          display: flex;
          justify-content: flex-end;
        }

        .crop-button {
          background-color: #4caf50;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .crop-button:hover {
          background-color: #45a049;
          box-shadow: 0 6px 8px rgba(0, 0, 0, 0.15);
          transform: translateY(-2px);
        }

        .img-container {
          position: relative;
          width: 10rem;
          height: 10rem;
          cursor: pointer;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .preview-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .file-input {
          display: none;
        }

        @media (max-width: 600px) {
          .my-cropper-container {
            width: 95%;
            margin: 0 10px;
            max-height: 95vh;
          }

          .cropper-area {
            min-height: 250px;
          }

          .cropper-header,
          .cropper-controls {
            padding: 12px 15px;
          }
        }
      `}</style>
    </>
  );
};

export default ImageCropper;
